import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Loader2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { CreativeUpload } from '@/features/campaigns/components/CreativeUpload';
import { AD_CATEGORIES } from '@/features/campaigns/constants/campaignOptions';
import { emptyManualForm } from '@/features/campaigns/types/campaign';
import type { CreativePreview } from '@/features/campaigns/types/campaign';
import { useUploadGalleryMutation } from '@/features/gallery/api/useGalleryQueries';
import { useDeleteFile } from '@/shared/lib/upload/useDeleteFile';

type UploadSlot = {
  mediaId: string;
  orientation: string;
  mediaUrl?: string;
  mediaType?: string;
};

const CONTENT_NAME_MIN = 3;
const CONTENT_NAME_MAX = 50;

type GalleryUploadModalProps = {
  open: boolean;
  onClose: () => void;
};

export function GalleryUploadModal({ open, onClose }: GalleryUploadModalProps) {
  const upload = useUploadGalleryMutation();
  const deleteFile = useDeleteFile();
  const [contentName, setContentName] = useState('');
  const [contentType, setContentType] = useState('');
  const [uploads, setUploads] = useState<Record<string, UploadSlot | null>>({
    PORTRAIT: null,
    LANDSCAPE: null,
  });
  const [previews, setPreviews] = useState<Record<string, CreativePreview>>({
    PORTRAIT: { url: '', type: '' },
    LANDSCAPE: { url: '', type: '' },
  });
  const dummyForm = useMemo(() => emptyManualForm(), []);
  const trimmedName = contentName.trim();
  const nameTooShort =
    trimmedName.length > 0 && trimmedName.length < CONTENT_NAME_MIN;
  const nameValid =
    trimmedName.length >= CONTENT_NAME_MIN &&
    trimmedName.length <= CONTENT_NAME_MAX;

  if (!open) return null;

  const mediaIds = (['PORTRAIT', 'LANDSCAPE'] as const)
    .map((o) => uploads[o])
    .filter(Boolean)
    .map((m) => ({
      mediaId: m!.mediaId,
      orientation: m!.orientation || 'LANDSCAPE',
    }));

  const canSubmit =
    nameValid &&
    Boolean(contentType) &&
    mediaIds.length === 2 &&
    !upload.isPending;

  const reset = (opts?: { deleteUploads?: boolean }) => {
    if (opts?.deleteUploads) {
      (['PORTRAIT', 'LANDSCAPE'] as const).forEach((orientation) => {
        const id = uploads[orientation]?.mediaId?.trim();
        if (id) {
          void deleteFile.mutateAsync({ mediaId: id }).catch(() => {});
        }
      });
    }
    setContentName('');
    setContentType('');
    setUploads({ PORTRAIT: null, LANDSCAPE: null });
    setPreviews({
      PORTRAIT: { url: '', type: '' },
      LANDSCAPE: { url: '', type: '' },
    });
  };

  const handleClose = () => {
    if (upload.isPending) return;
    reset({ deleteUploads: true });
    onClose();
  };

  const handleSubmit = () => {
    if (!trimmedName) {
      toast.error('Content name is required');
      return;
    }
    if (trimmedName.length < CONTENT_NAME_MIN) {
      toast.error(`Content name must be at least ${CONTENT_NAME_MIN} characters`);
      return;
    }
    if (trimmedName.length > CONTENT_NAME_MAX) {
      toast.error(`Content name cannot exceed ${CONTENT_NAME_MAX} characters`);
      return;
    }
    if (!contentType) {
      toast.error('Content type is required');
      return;
    }
    if (mediaIds.length < 2) {
      toast.error('Both portrait and landscape creatives are required');
      return;
    }

    upload.mutate(
      {
        contentName: trimmedName,
        contentType,
        mediaIds,
      },
      {
        onSuccess: () => {
          reset();
          onClose();
        },
      },
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Close overlay"
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
      />
      <div className="relative z-10 flex h-[min(92vh,820px)] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl border border-[var(--dash-line)] bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-3xl">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--dash-line)] px-5 py-4">
          <div className="text-left">
            <h2 className="m-0 text-lg font-semibold text-[var(--dash-ink)]">
              Upload to Pre-Approved Gallery
            </h2>
            <p className="mt-1 mb-0 text-sm text-[var(--dash-muted)]">
              Add content in both portrait and landscape
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full border border-[var(--dash-line)] p-2 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <label className="mb-1.5 block text-left text-sm font-semibold text-[var(--dash-ink)]">
            Content name <span className="text-red-500">*</span>
          </label>
          <input
            value={contentName}
            onChange={(e) =>
              setContentName(e.target.value.slice(0, CONTENT_NAME_MAX))
            }
            maxLength={CONTENT_NAME_MAX}
            minLength={CONTENT_NAME_MIN}
            placeholder="Name for your reference"
            aria-invalid={nameTooShort}
            className={`w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-[var(--dash-ink)] outline-none focus:ring-2 ${
              nameTooShort
                ? 'border-red-400 focus:border-red-400 focus:ring-red-200'
                : 'border-[var(--dash-line)] focus:border-[var(--dash-brand)] focus:ring-[var(--dash-brand)]/15'
            }`}
          />
          <div className="mb-4 mt-1.5 flex items-center justify-between gap-3">
            <p
              className={`m-0 text-left text-[11px] ${
                nameTooShort
                  ? 'font-semibold text-red-500'
                  : 'text-[var(--dash-muted)]'
              }`}
            >
              {nameTooShort
                ? `Minimum ${CONTENT_NAME_MIN} characters`
                : `Min ${CONTENT_NAME_MIN} characters`}
            </p>
            <span
              className={`shrink-0 text-[11px] tabular-nums ${
                contentName.length >= CONTENT_NAME_MAX
                  ? 'font-semibold text-[var(--dash-brand)]'
                  : 'text-[var(--dash-muted)]'
              }`}
            >
              {contentName.length}/{CONTENT_NAME_MAX}
            </span>
          </div>

          <label className="mb-1.5 block text-left text-sm font-semibold text-[var(--dash-ink)]">
            Content type <span className="text-red-500">*</span>
          </label>
          <div className="relative mb-4">
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="w-full cursor-pointer appearance-none rounded-xl border border-[var(--dash-line)] bg-white py-2.5 pr-9 pl-3.5 text-sm text-[var(--dash-ink)] outline-none focus:border-[var(--dash-brand)] focus:ring-2 focus:ring-[var(--dash-brand)]/15"
            >
              <option value="" disabled>
                Select content type
              </option>
              {Object.entries(AD_CATEGORIES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-[var(--dash-muted)]"
              aria-hidden
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(['PORTRAIT', 'LANDSCAPE'] as const).map((orientation) => (
              <div
                key={orientation}
                className="rounded-2xl border border-dashed border-[var(--dash-line)] bg-[var(--dash-soft)]/40 p-3"
              >
                <p className="mb-2 text-[11px] font-bold tracking-wide text-[var(--dash-muted)] uppercase">
                  {orientation} <span className="text-red-500">*</span>
                </p>
                <CreativeUpload
                  campaignData={dummyForm}
                  creativePreview={previews[orientation]}
                  setCreativePreview={(updater) => {
                    setPreviews((prev) => {
                      const current = prev[orientation] || {
                        url: '',
                        type: '',
                      };
                      const next =
                        typeof updater === 'function'
                          ? updater(current)
                          : updater;
                      if (!next.url) {
                        setUploads((u) => ({ ...u, [orientation]: null }));
                      }
                      return { ...prev, [orientation]: next };
                    });
                  }}
                  hideDuration
                  compact
                  enableGallery={false}
                  forcedOrientation={orientation}
                  onUploaded={(item) => {
                    setUploads((prev) => ({ ...prev, [orientation]: item }));
                  }}
                />
              </div>
            ))}
          </div>

          {mediaIds.length === 1 ? (
            <p className="mt-3 mb-0 text-center text-xs font-semibold text-red-500">
              Both portrait and landscape files are required.
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--dash-line)] bg-white px-5 py-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={upload.isPending}
            className="rounded-xl border border-[var(--dash-line)] bg-white px-4 py-2.5 text-sm font-semibold text-[var(--dash-ink)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="inline-flex min-w-[148px] items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-55"
            style={{
              background:
                'linear-gradient(90deg, #8E58A6 0%, #5A4EA6 25%, #145E8C 50%, #007A78 75%, #33A65B 100%)',
            }}
          >
            {upload.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading…
              </>
            ) : (
              'Upload content'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
